import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PreviewPanel = ({ jobData, onClose }) => {
  const formatSalary = () => {
    if (!jobData?.salaryMin && !jobData?.salaryMax) return 'Salary not disclosed';
    if (jobData?.salaryMin === '0' && jobData?.salaryMax === '0') return 'Unpaid';
    if (jobData?.salaryMin === jobData?.salaryMax) return `₹${jobData?.salaryMin} LPA`;
    return `₹${jobData?.salaryMin || '0'} - ₹${jobData?.salaryMax || '0'} LPA`;
  };

  const formatExperience = () => {
    if (!jobData?.experienceMin && !jobData?.experienceMax) return 'Fresher';
    if (jobData?.experienceMin === jobData?.experienceMax) return `${jobData?.experienceMin} years`;
    return `${jobData?.experienceMin || '0'} - ${jobData?.experienceMax || '0'} years`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-heading font-semibold text-card-foreground">
            Job Listing Preview
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-md hover:bg-muted transition-smooth"
          >
            <Icon name="X" size={20} color="var(--color-muted-foreground)" />
          </button>
        </div>

        <div className="p-6">
          <div className="bg-background border border-border rounded-lg p-6">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center">
                <Icon name="Briefcase" size={32} color="var(--color-primary)" />
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-heading font-bold text-card-foreground mb-2">
                  {jobData?.title || 'Job Title'}
                </h3>
                <p className="text-lg text-card-foreground font-medium mb-2">
                  {jobData?.company || 'Company Name'}
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-primary/10 text-primary">
                    {jobData?.type}
                  </span>
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-secondary/10 text-secondary">
                    {jobData?.workArrangement?.charAt(0)?.toUpperCase() + jobData?.workArrangement?.slice(1)}
                  </span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="flex items-center gap-2">
                <Icon name="MapPin" size={16} color="var(--color-muted-foreground)" />
                <span className="text-sm text-muted-foreground">
                  {jobData?.location || 'Location'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="Briefcase" size={16} color="var(--color-muted-foreground)" />
                <span className="text-sm text-muted-foreground">
                  {formatExperience()}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="IndianRupee" size={16} color="var(--color-muted-foreground)" />
                <span className="text-sm text-muted-foreground">
                  {formatSalary()}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="Calendar" size={16} color="var(--color-muted-foreground)" />
                <span className="text-sm text-muted-foreground">
                  {jobData?.applicationDeadline ? new Date(jobData?.applicationDeadline)?.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'No deadline'}
                </span>
              </div>
            </div>

            {jobData?.companyDescription && (
              <div className="mb-6">
                <h4 className="text-lg font-medium text-card-foreground mb-2">About Company</h4>
                <p className="text-sm text-muted-foreground whitespace-pre-line">
                  {jobData?.companyDescription}
                </p>
              </div>
            )}

            {jobData?.responsibilities && (
              <div className="mb-6">
                <h4 className="text-lg font-medium text-card-foreground mb-2">Responsibilities</h4>
                <p className="text-sm text-muted-foreground whitespace-pre-line">
                  {jobData?.responsibilities}
                </p>
              </div>
            )}

            {jobData?.qualifications && (
              <div className="mb-6">
                <h4 className="text-lg font-medium text-card-foreground mb-2">Qualifications</h4>
                <p className="text-sm text-muted-foreground whitespace-pre-line">
                  {jobData?.qualifications}
                </p>
              </div>
            )}

            {jobData?.requiredSkills?.length > 0 && (
              <div className="mb-6">
                <h4 className="text-lg font-medium text-card-foreground mb-2">Required Skills</h4>
                <div className="flex flex-wrap gap-2">
                  {jobData?.requiredSkills?.map((skill, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-muted border border-border rounded text-sm text-card-foreground"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {jobData?.isInternship && jobData?.internshipDuration && (
              <div className="mb-6 p-4 bg-accent/10 border border-accent/20 rounded-lg">
                <h4 className="text-lg font-medium text-card-foreground mb-3">Internship Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Duration</p>
                    <p className="text-sm font-medium text-card-foreground">
                      {jobData?.internshipDuration?.replace('-', ' ')?.replace(/\b\w/g, (l) => l?.toUpperCase())}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Stipend</p>
                    <p className="text-sm font-medium text-card-foreground">
                      {jobData?.stipendAmount === '0' ? 'Unpaid' : `₹${jobData?.stipendAmount}/month`}
                    </p>
                  </div>
                </div>
                {jobData?.hasMentorship && (
                  <div className="mt-3">
                    <p className="text-sm text-muted-foreground mb-1">Mentorship Program</p>
                    <p className="text-sm text-card-foreground whitespace-pre-line">
                      {jobData?.mentorshipDetails || 'Mentorship program available'}
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3">
              <Button fullWidth iconName="Send" iconPosition="left">
                Apply Now
              </Button>
              <Button variant="outline" iconName="Bookmark" iconPosition="left">
                Save
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreviewPanel;